import {
  ErrorOutlineRounded,
  NewReleasesRounded,
  Refresh,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  IconButton as MuiIconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  NotificationAPI,
  OrganizationAPI,
  MaintenanceMessageAPI,
} from "../../../api";
import PATHS from "../../../components/navigation/_paths";
import { useGlobalOrganizationContext } from "../../../hooks/useGlobalOrganizationContext";
import { currentUserId } from "../../../lib/auth";
import {
  getNotificationPermission,
  requestNotificationPermissionIfRequired,
} from "../../../lib/notification";
import {
  BannerPriority,
  MaintenanceMessage,
} from "../../../model/MaintenanceMessage";
import { BillingState } from "../../../model/OrganizationBillingState";
import OrganizationStripeSetupDialog from "./dialogs/OrganizationStripeSetupDialog";

export interface BannerMessage {
  id: string;
  content: React.ReactNode;
  color?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  priority?: BannerPriority;
  permanent?: boolean;
  refreshSeconds?: number;
}

interface OrganizationMessageBannerProps {
  messages?: BannerMessage[];
}

const STRIPE_BANNER_ID = `stripe-billing-banner`;
const NOTIF_BANNER_ID = `notif-banner`;
const BROWSER_NOTIF_BANNER_ID = `browser-notif-banner`;

const OrganizationMessageBanner: React.FC<OrganizationMessageBannerProps> = ({
  messages,
}: OrganizationMessageBannerProps) => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Stripe stuff
  const { organizationBillingState, organization } =
    useGlobalOrganizationContext();
  const currentBillingState = organizationBillingState
    ? organizationBillingState.billing_state
    : BillingState.VALID;
  const hasStripeId = organizationBillingState?.stripe_id != null;
  const [forceRefreshResult, setForceRefreshResult] = useState(false);
  const [setupStripeDialogOpen, setSetupStripeDialogOpen] =
    useState<boolean>(false);
  const [maintenanceMessages, setMaintenanceMessages] = useState<
    MaintenanceMessage[] | undefined
  >();
  const forceRefreshSubscription = useCallback(() => {
    if (!organization) {
      return;
    }
    OrganizationAPI.forceRefreshSubscription(organization.id).then((result) => {
      setForceRefreshResult(result);
    });
  }, [organization]);

  // Notification stuff
  const notificationPermission = getNotificationPermission();

  // Message composition
  const allMessages = useMemo(() => {
    return messages ? [...messages] : [];
  }, [messages]);

  const now = Date.now();

  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!organization) return;

      try {
        const notificationState =
          await NotificationAPI.getNotificationsByUserAndOrganization(
            currentUserId(),
            organization.id,
          );
        setNotificationsEnabled(notificationState.enabled);

        const loadedMessages = await MaintenanceMessageAPI.getAll();
        setMaintenanceMessages(loadedMessages ?? []);
      } catch (error) {
        console.error("Failed to fetch notification state:", error);
        setNotificationsEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [organization]);

  // Banner state stuff (with expiry)
  type ClosedMap = { [id: string]: number };
  const [closedMap, setClosedMap] = useState<{ [id: string]: number }>(() => {
    const raw = localStorage.getItem("org-banner-closed-map");
    return raw ? JSON.parse(raw) : {};
  });

  useEffect(() => {
    const closedRaw = localStorage.getItem("org-banner-closed-map");
    let parsed: ClosedMap = {};
    if (closedRaw) {
      try {
        parsed = JSON.parse(closedRaw);
      } catch {
        //
      }
    }
    // Clean up closedMap: remove entries for banners that are not in allMessages or are expired forever
    const validIds = new Set([
      ...(messages ? messages.map((m) => m.id) : []),
      ...(maintenanceMessages ? maintenanceMessages.map((m) => m.id) : []),
      STRIPE_BANNER_ID,
      NOTIF_BANNER_ID,
      BROWSER_NOTIF_BANNER_ID,
    ]);

    const cleaned: ClosedMap = {};
    for (const [id, ts] of Object.entries(parsed)) {
      // Only keep if the id is still valid and (refreshSeconds is not 0/undefined or not expired forever)
      let refreshSeconds: number | undefined = undefined;
      if (allMessages.find((m) => m.id === id)) {
        const msg = allMessages.find((m) => m.id === id);
        refreshSeconds = msg?.refreshSeconds;
      } else if (
        id === STRIPE_BANNER_ID ||
        id === NOTIF_BANNER_ID ||
        id === BROWSER_NOTIF_BANNER_ID
      ) {
        // These are always present, use default values
        refreshSeconds = id === STRIPE_BANNER_ID ? 1 : 86400;
      }

      // Remove if not valid or never comes back
      if (!validIds.has(id)) {
        continue;
      }
      if (refreshSeconds === undefined) {
        continue;
      }

      // Otherwise, keep
      cleaned[id] = ts;
    }
    if (
      maintenanceMessages !== undefined &&
      Object.keys(cleaned).length !== Object.keys(parsed).length
    ) {
      localStorage.setItem("org-banner-closed-map", JSON.stringify(cleaned));
    }
    setClosedMap(cleaned);
  }, [messages, allMessages, maintenanceMessages]);

  const handleClose = (id: string) => {
    const updated = { ...closedMap, [id]: Date.now() };
    setClosedMap(updated);
    localStorage.setItem("org-banner-closed-map", JSON.stringify(updated));
  };

  if (loading) return null;

  // This message will appear daily until the user enables notifications
  if (
    !notificationsEnabled &&
    !allMessages.find((m) => m.id === NOTIF_BANNER_ID)
  ) {
    allMessages.push({
      id: NOTIF_BANNER_ID,
      color: theme.palette.warning.main,
      icon: <ErrorOutlineRounded sx={{ mr: 1 }} />,
      priority: "high",
      content: (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography component="span">
              {"You have notifications disabled!"}
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              sx={{ py: 0, px: 1, borderRadius: 1, ml: 1 }}
              onClick={() => {
                navigate(PATHS.PROFILE_PREFERENCES.link);
              }}
            >
              Manage Preferences
            </Button>
          </Box>
        </Box>
      ),
      permanent: false,
      refreshSeconds: 86400,
    });
  }

  if (maintenanceMessages && maintenanceMessages.length > 0) {
    maintenanceMessages.forEach((m) => {
      let refreshSeconds = 0;
      if (m.expiration) {
        const now = Date.now();
        const exp = new Date(m.expiration).getTime();
        refreshSeconds = Math.max(0, Math.floor((exp - now) / 1000));
      }
      allMessages.push({
        id: m.id,
        content: <Typography ml={1}>{m.content}</Typography>,
        color: m.color,
        icon: <NewReleasesRounded />,
        priority: m.priority,
        permanent: false,
        refreshSeconds,
      } as BannerMessage);
    });
  }

  if (
    notificationPermission !== "granted" &&
    !allMessages.find((m) => m.id === BROWSER_NOTIF_BANNER_ID)
  ) {
    allMessages.push({
      id: BROWSER_NOTIF_BANNER_ID,
      color: theme.palette.warning.main,
      icon: <ErrorOutlineRounded sx={{ mr: 1 }} />,
      priority: "medium",
      content:
        notificationPermission === "default" ? (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography component="span">
                {"Notifications are not enabled on your browser"}
              </Typography>
              <Button
                variant="outlined"
                color="inherit"
                sx={{ py: 0, px: 1, borderRadius: 1, ml: 1 }}
                onClick={() => {
                  requestNotificationPermissionIfRequired();
                  handleClose(BROWSER_NOTIF_BANNER_ID);
                }}
              >
                Click to enable
              </Button>
            </Box>
          </Box>
        ) : (
          "Notifications are blocked on your browser! Please enable them in your browser settings."
        ),
      permanent: false,
      refreshSeconds: 86400,
    });
  }

  if (
    currentBillingState !== BillingState.VALID &&
    !forceRefreshResult &&
    !allMessages.find((m) => m.id === STRIPE_BANNER_ID) &&
    !(
      STRIPE_BANNER_ID in closedMap &&
      (allMessages.find((m) => m.id === STRIPE_BANNER_ID)?.refreshSeconds ===
        0 ||
      allMessages.find((m) => m.id === STRIPE_BANNER_ID)?.refreshSeconds ===
        undefined
        ? true
        : now - closedMap[STRIPE_BANNER_ID] <
          (allMessages.find((m) => m.id === STRIPE_BANNER_ID)?.refreshSeconds ??
            0) *
            1000)
    )
  ) {
    allMessages.push({
      id: STRIPE_BANNER_ID,
      color:
        currentBillingState === BillingState.INVALID
          ? (theme.palette.error.main ?? "#d32f2f") // fallback error color
          : (theme.palette.warning.main ?? "#ed6c02"), // fallback warning color
      icon: <ErrorOutlineRounded sx={{ mr: 1 }} />,
      priority: "low",
      content: (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography component="span">
              {hasStripeId
                ? currentBillingState === BillingState.INVALID
                  ? "Your subscription is no longer active!"
                  : "Your subscription needs to be renewed soon!"
                : "You do not have an active subscription!"}
            </Typography>
            {hasStripeId ? (
              <Button
                variant="outlined"
                color="inherit"
                sx={{ py: 0, px: 1, borderRadius: 1, ml: 1 }}
                onClick={() => {
                  navigate(PATHS.ORGANIZATION_MANAGE_BILLING.link);
                  forceRefreshSubscription();
                }}
              >
                Manage Subscription
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="inherit"
                sx={{ py: 0, px: 1, borderRadius: 1, ml: 1 }}
                onClick={() => setSetupStripeDialogOpen(true)}
              >
                Setup Subscription
              </Button>
            )}
          </Box>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
            <IconButton
              size="small"
              sx={{ color: "inherit", p: 0 }}
              onClick={forceRefreshSubscription}
            >
              <Refresh />
            </IconButton>
          </Box>
        </Box>
      ),
      permanent: true,
      refreshSeconds: 1,
    });
  }

  const priorityOrder = ["highest", "high", "mid", "medium", "low", "lowest"];
  const visibleMessage = allMessages
    .filter((msg) => {
      const ts = closedMap[msg.id];
      const refreshSeconds = msg.refreshSeconds;
      // If refreshSeconds is 0, never show again after close
      if (refreshSeconds === 0) {
        return !ts;
      }
      // If refreshSeconds is undefined, use default (never comes back)
      if (refreshSeconds === undefined) {
        return !ts;
      }
      return !ts || now - ts >= refreshSeconds * 1000;
    })
    .sort(
      (a, b) =>
        priorityOrder.indexOf(a.priority || "low") -
        priorityOrder.indexOf(b.priority || "low"),
    )[0];

  return (
    <>
      {visibleMessage &&
        maintenanceMessages &&
        (() => {
          const bgColor =
            typeof visibleMessage.color === "string"
              ? visibleMessage.color
              : theme.palette.warning.main;
          const textColor = theme.palette.getContrastText
            ? theme.palette.getContrastText(bgColor)
            : theme.palette.text.primary;
          return (
            <Box
              key={visibleMessage.id}
              display="flex"
              alignItems="center"
              position="relative"
              padding={1}
              sx={{
                bgcolor: bgColor,
                color: textColor,
              }}
            >
              {visibleMessage.icon}
              <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                {visibleMessage.content}
              </Box>
              {!visibleMessage.permanent && (
                <MuiIconButton
                  size="small"
                  onClick={() => handleClose(visibleMessage.id)}
                  sx={{
                    color: textColor,
                    ml: 1,
                    position: "absolute",
                    right: 5,
                    top: 5,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </MuiIconButton>
              )}
            </Box>
          );
        })()}
      {organization && (
        <OrganizationStripeSetupDialog
          title={"Setup Stripe Subscription"}
          open={setupStripeDialogOpen}
          handleConfirm={async () => {
            setSetupStripeDialogOpen(false);
            forceRefreshSubscription();
          }}
          handleCancel={() => {
            setSetupStripeDialogOpen(false);
          }}
          organization={organization}
        />
      )}
    </>
  );
};

export default OrganizationMessageBanner;
